import {
  IGetPriceEstimateRequest,
  IGetPriceEstimateResponse,
} from "@api-clients/checkout";

import {
  getDefaultDiscountForTrialPlan,
  getDiscountCode,
  normaliseDiscount,
  type TDiscount,
  type TSubscriptionData,
} from "@./suf-store";

export {
  getDefaultDiscountForTrialPlan,
  getDiscountCode,
  normaliseDiscount,
  normalisePlanType,
} from "@./suf-store";

export const resolveEffectiveDiscountCode = (
  requestedDiscountCode: IGetPriceEstimateRequest["discountCode"],
  subscription: TSubscriptionData,
): string | undefined => {
  const requestCode = getDiscountCode(requestedDiscountCode);
  const storeCode = getDiscountCode(subscription.discount);
  const defaultDiscountForPlan = getDefaultDiscountForTrialPlan(subscription.trial?.planType);

  return requestCode ?? storeCode ?? defaultDiscountForPlan?.discountCode;
};

export const normaliseApiDiscountToStoreDiscount = (
  discount: IGetPriceEstimateResponse["discount"] | undefined,
  subscription: TSubscriptionData,
): TSubscriptionData["discount"] | undefined => normaliseDiscount(
  discount?.code
    ? {
      discountCode: discount.code,
      type: discount.discountType,
      value: discount.value,
    }
    : subscription.discount,
  getDefaultDiscountForTrialPlan(subscription.trial?.planType),
) as TDiscount | undefined;

export const hasDiscountChanged = (
  currentDiscount: unknown,
  nextDiscount: TSubscriptionData["discount"] | undefined,
): boolean => {
  if (!nextDiscount) {
    return false;
  }

  if (typeof currentDiscount === "string") {
    return true;
  }

  const isDiscountObject = currentDiscount && typeof currentDiscount === "object"
    && !Array.isArray(currentDiscount);
  const currentDiscountMetadata = isDiscountObject
    ? currentDiscount as Partial<TDiscount>
    : undefined;

  return (
    getDiscountCode(currentDiscount) !== getDiscountCode(nextDiscount)
    || currentDiscountMetadata?.type !== nextDiscount.type
    || currentDiscountMetadata?.value !== nextDiscount.value
  );
};

/**
 * Returns a partial subscription update when the persisted discount needs
 * normalising (e.g. legacy string format), or `undefined` when no change
 * is needed.
 */
export const getRehydratedSubscriptionUpdate = (
  subscription: TSubscriptionData,
): Partial<TSubscriptionData> | undefined => {
  const defaultDiscount = getDefaultDiscountForTrialPlan(subscription.trial?.planType);
  const normalisedDiscount = normaliseDiscount(subscription.discount, defaultDiscount) as TDiscount | undefined;

  if (hasDiscountChanged(subscription.discount, normalisedDiscount)) {
    return { discount: normalisedDiscount };
  }

  return undefined;
};
